import React, { memo, useState } from 'react';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import {
  PersonAdd as PersonAddIcon,
  DeleteOutlined as DeleteOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
} from '@material-ui/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from './devices.styles';
import { Typography } from '../../components/Typography';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { Dropdown } from '../../components/Dropdown';
import { NoDataLayout } from '../../components/layout';
import { emptyUsersViewIcon } from '../../assets/icons';
import {
  AddDeviceModal,
  DeleteDeviceModal,
  EditDeviceModal,
  ResetDeviceModal,
} from './components';
import { IRequestParams } from '../../components/types';
import { DeviceGrid } from '../../components/device-grid/device-grid';

interface IDevicesView extends WithStyles<typeof styles> {
  selectDevice: any;
  devicesList: any;
  permissions: any;
  handleAddDevice: (data: any) => void;
  handleDeleteDevice: (id: any) => void;
  handleResetDevice: (id: string) => void;
  handleEditDevice: (data: { id: string; name: string }) => void;
  handleGetDevices: ({ limit, offset, sortOrder }: IRequestParams) => void;
}

const DevicesView: React.FC<IDevicesView> = ({
  classes,
  selectDevice,
  devicesList,
  permissions,
  handleAddDevice,
  handleDeleteDevice,
  handleResetDevice,
  handleEditDevice,
  // handleGetDevices,
}: IDevicesView) => {
  const [openAddDeviceModal, setOpenAddDeviceModal] = useState(false);
  const [openEditDeviceModal, setOpenEditDeviceModal] = useState(false);
  const [openDeleteDeviceModal, setOpenDeleteDeviceModal] = useState(false);
  const [openResetDeviceModal, setOpenResetDeviceModal] = useState(false);
  const [openMoreOption, setOpenMoreOption] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const intl = useIntl();

  const DeviceOptionList = [
    {
      label: intl.formatMessage({
        id: 'unassign_this_device',
      }),
      id: 'device-unassign_this_device',
      onClick: () => setOpenDeleteDeviceModal(true),
      icon: <DeleteOutlinedIcon fontSize="small" />,
    },
    {
      label: intl.formatMessage({
        id: 'edit_this_device',
      }),
      id: 'device-edit_this_device',
      onClick: () => setOpenEditDeviceModal(true),
      icon: <EditOutlinedIcon fontSize="small" />,
    },
  ];

  const handleOpenMoreOption = (
    event: React.MouseEvent<HTMLElement>,
    data: any
  ) => {
    setSelectedDevice(data);
    setOpenMoreOption(!openMoreOption);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMoreOption = (event: React.MouseEvent<EventTarget>) => {
    // setAnchorEl(null);
    setOpenMoreOption(false);
  };

  const handleOpenResetModal = (
    event: React.MouseEvent<HTMLElement>,
    data: any
  ) => {
    setSelectedDevice(data);
    setOpenResetDeviceModal(!openResetDeviceModal);
    setAnchorEl(event.currentTarget);
  };

  const renderAddDeviceButton = () => {
    return (
      <div>
        <Button
          customStyles={classes.button}
          onClick={() => setOpenAddDeviceModal(true)}
          label={intl.formatMessage({
            id: 'add_device',
          })}
          id="add-device-button"
          startIcon={<PersonAddIcon />}
          variant="contained"
          color="primary"
        />
      </div>
    );
  };
  if (!devicesList) {
    return (
      <NoDataLayout image={emptyUsersViewIcon} heading="Add your first device">
        {permissions.addDevices ? renderAddDeviceButton() : null}
        <AddDeviceModal
          selectDevice={selectDevice}
          open={openAddDeviceModal}
          handleClose={() => setOpenAddDeviceModal(false)}
          handleAddDevice={handleAddDevice}
        />
      </NoDataLayout>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.topWrapper} id="devices-view">
        <Typography variant="h3" component="h2" color="textPrimary">
          <FormattedMessage id="devices" />
        </Typography>
        <div className={classes.buttonWrapper}>
          {permissions.addDevices ? renderAddDeviceButton() : null}
        </div>
      </div>
      <div className={classes.bottomWrapper}>
        <Card customStyles={classes.card}>
          <DeviceGrid
            onResetClick={handleOpenResetModal}
            onOpenMoreClick={handleOpenMoreOption}
            onDeleteDeviceClick={handleDeleteDevice}
            devices={devicesList.data}
            isLoading={selectDevice.loading}
            columnNames={[
              'id',
              'name',
              'tvAvailable',
              'PrivacyEnabled',
              'PrivacyTimeOut',
              'rebootStatus',
              'version',
              'reset',
              'more',
            ]}
            id="devices-list-table"
          />
        </Card>
      </div>
      <AddDeviceModal
        selectDevice={selectDevice}
        open={openAddDeviceModal}
        handleClose={() => setOpenAddDeviceModal(false)}
        handleAddDevice={handleAddDevice}
      />
      <DeleteDeviceModal
        selectDevice={selectDevice}
        open={openDeleteDeviceModal}
        handleClose={() => setOpenDeleteDeviceModal(false)}
        selectedDevice={selectedDevice}
        handleDeleteDevice={handleDeleteDevice}
      />
      <ResetDeviceModal
        selectDevice={selectDevice}
        open={openResetDeviceModal}
        handleClose={() => setOpenResetDeviceModal(false)}
        selectedDevice={selectedDevice}
        handleResetDevice={handleResetDevice}
      />
      <EditDeviceModal
        selectDevice={selectDevice}
        open={openEditDeviceModal}
        handleClose={() => setOpenEditDeviceModal(false)}
        selectedDevice={selectedDevice}
        handleEditDevice={handleEditDevice}
      />
      <Dropdown
        id="device-more-option-button"
        open={Boolean(selectedDevice) && openMoreOption}
        anchorRef={anchorEl}
        handleClose={handleCloseMoreOption}
        itemList={DeviceOptionList}
      />
    </div>
  );
};

export default memo(withStyles(styles)(DevicesView));
